import { createTheme, PaletteMode } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { StrokeCap } from '@rive-app/canvas/rive_advanced.mjs';

export const useAppTheme = (
  mode: PaletteMode,
  dir: 'rtl' | 'ltr' = 'rtl',
  language: 'he' | 'en'
) => {
  // Step 1: create the base theme (so we can reuse its palette etc.)
  const baseTheme = createTheme({
    direction: dir,
    palette: {
      mode,
      primary: {
        main: '#188A92', // Bright Blue/Green
        dark: '#223f5c',
        light: '#f9f9f9',
        contrastText: '#fff',
      },
      secondary: {
        main: '#c1bcb1', // light between
        dark: '#847D6E', // Grey-brown
        light: '#f8f7f4',
        contrastText: '#fff',
      },
      background: {
        default: '#EAF0FF', // Very Light Blue
        paper: '#ffffff',
      },
      text: {
        primary: '#2E2F2F', // Graphite
        secondary: '#001C30', // Blue Graphite
        disabled: '#bcc5ce',
      },
      custom: {
        mainDark: '#0b464a',
      },
    } as any, // to allow custom property
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 1024,
        lg: 1200,
        xl: 1380,
      },
    },
    typography: {
      fontFamily: dir === 'rtl' ? "'Noto Sans Hebrew', sans-serif" : "'Montserrat', sans-serif",
      h1: { fontSize: '3.2rem', fontWeight: 700 },
      h2: { fontSize: '2.5rem', fontWeight: 500 },
      h3: { fontSize: '1.8rem', fontWeight: 500 },
      h4: { fontSize: '1.6rem', fontWeight: 600 },
      h5: { fontSize: '1.5rem', fontWeight: 500, marginBottom: '1rem' },
      h6: { fontSize: '1.4rem', fontWeight: 500 },
      body1: { fontSize: '1.5rem', fontWeight: 500, lineHeight: 1.5 },
      body2: { fontSize: '1.3rem', fontWeight: 400, lineHeight: 1.5 },
      button: { fontSize: '1.5rem', textTransform: 'none' },
    },
  });

  // Step 2: extend the base theme with components (using access to theme.palette)
  const appTheme = createTheme(baseTheme, {
    components: {
      MuiButton: {
        styleOverrides: {
          root: ({ ownerState }) => ({
            ...(ownerState.size === 'small' && {
              borderRadius: '5rem',
              fontSize: '1.3rem',
              color: baseTheme.palette.secondary.dark,
              padding: '0.2rem 1.2rem 0.2rem',
              border: `1px solid ${baseTheme.palette.grey[300]}`,
              marginBlockEnd: '-4px',
              '& > span': {
                marginInlineStart: '6px',
                '& > svg': {
                  width: '1.5rem',
                },
              },
            }),
            ...(ownerState.size === 'medium' && {
              borderRadius: '5rem',
              padding: '0.2rem 3rem 0.4rem',
              border: `1px solid ${baseTheme.palette.primary.dark}`,
            }),
            ...(ownerState.size === 'large' && {
              borderRadius: '5rem',
              fontSize: '1.7rem',
              padding: '0.5rem 4rem',
              // border: `1px solid ${baseTheme.palette.primary.dark}`,
            }),
          }),
          containedPrimary: {
            color: '#ffffff',
            borderColor: baseTheme.palette.secondary.dark,
            boxShadow: 'none',
            borderWidth: '0px',
            backgroundColor: baseTheme.palette.primary.dark,
            '&:hover': {
              boxShadow: '0 0 2px #847d6e',
            },
            '&:disabled': {
              color: 'rgba(255,255,255,0.5)',
            },
          },
          outlinedPrimary: {
            backgroundColor: '#ffffff',
            color: baseTheme.palette.secondary.dark,
            border: `1px solid ${baseTheme.palette.grey[300]}`,
            '&:hover': {
              backgroundColor: baseTheme.palette.secondary.light,
            },
          },
          text: {
            backgroundColor: '#ffffff',
            color: baseTheme.palette.secondary.dark,
            border: `1px solid ${baseTheme.palette.grey[300]}`,
            height: '3rem',
            '&:hover': {
              backgroundColor: baseTheme.palette.secondary.light,
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.palette.primary.main,
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            marginTop: '0',
            '& .MuiInputBase-root': {
              borderRadius: '0.5rem',
              backgroundColor: '#ffffff',
              fontSize: '1.8rem',
              fontWeight: '400',
              // height: '4rem',
              '&.Mui-focused': {
                backgroundColor: '#f8f7f4',
              },
            },
            '& .MuiInputBase-root:not(.MuiInputBase-multiline)': {
              height: '4rem', // only single-line inputs
            },
            '& .MuiFormHelperText-root': {
              marginTop: '0.5rem',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: baseTheme.palette.secondary.main,
              '&:hover': {
                borderColor: baseTheme.palette.secondary.dark,
              },
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: baseTheme.palette.secondary.dark,
              borderWidth: '1px',
            },
            '&.datepicker ': {
              '& .MuiOutlinedInput-root': {
                fontSize: '1.3rem',
                fontWeight: '400',
                lineHeight: '1.75',
                height: '3.3rem',
                paddingInlineStart: '1rem',
                paddingInlineEnd: '2rem',
                marginBlockEnd: '-4px',
                '& fieldset': {
                  borderRadius: '5rem',
                  borderColor: baseTheme.palette.grey[300],
                },
              },
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: baseTheme.palette.text.primary,
            // '&.MuiTypography-h4, &.MuiTypography-h2, &.MuiTypography-h1,&.MuiTypography-h3': {
            //   color: baseTheme.palette.primary.dark,
            // },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: baseTheme.palette.primary.dark,
            fontWeight: '400',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: baseTheme.palette.primary.dark,
            marginBlockStart: '2rem',
            '&.Mui-selected': {
              color: baseTheme.palette.primary.dark,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: baseTheme.palette.primary.dark,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            width: '100%',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: '#ffffff',
            borderRadius: '1rem',
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderWidth: '1px ',
            width: '100%',
            height: '1px',
            marginBlock: '1rem',
            borderColor: 'transparent',
            borderBottomColor: '#b9cace61',
            '&.MuiDivider-vertical': {
              width: '1px',
              height: '100%',
              borderRightColor: '#b9cace61',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontSize: '1.3rem',
            marginTop: '0.3rem',
          },
        },
      },
      MuiPickersPopper: {
        styleOverrides: {
          root: {
            zIndex: 1300,
            '& span': {
              fontSize: '1.3rem',
              color: baseTheme.palette.primary.main,
              fontWeight: '600',
            },
            '& .MuiPickersDay-root': {
              fontSize: '1.3rem',
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            width: '3.5rem',
            padding: '0rem',
            marginInlineEnd: '0rem',
            color: baseTheme.palette.primary.main,
            '& svg': {
              fontSize: '2rem',
              '& > path': {
                d: `path('M11 21C16.5228 21 21 16.5228 21 11C21 5.47715 16.5228 1 11 1C5.47715 1 1 5.47715 1 11C1 16.5228 5.47715 21 11 21Z')`,
                fill: 'none',
                stroke: baseTheme.palette.primary.main,
                strokeWidth: '1.5',
              },
            },
            '&.Mui-checked': {
              color: baseTheme.palette.primary.main,
              '& svg': {
                fontSize: '2rem',
                '& > path': {
                  d: `path('m6.5 11 3 3 6-6m5.5 3c0 5.523-4.477 10-10 10S1 16.523 1 11 5.477 1 11 1s10 4.477 10 10Z')`,
                  fill: 'none',
                  stroke: baseTheme.palette.primary.main,
                  strokeWidth: '1.5',
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                },
              },
            },
            '&.Mui-disabled': {
              color: baseTheme.palette.text.disabled,
              '& svg': {
                Stroke: baseTheme.palette.text.disabled,
                '& > path': {
                  stroke: baseTheme.palette.text.disabled,
                },
              },
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginInline: '0',
          },
        },
      },
      MuiTablePagination: {
        defaultProps: {
          labelRowsPerPage: 'שורות לעמוד', // override "Rows per page"
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            '& .MuiDataGrid-cell': {
              fontSize: '1.4rem',
            },
          },
        },
      },
    },
  });

  return appTheme;
};
